import {
  createContext,
  createElement,
  useContext,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  Fragment,
  useEffect,
} from 'react';
import { Dialog } from '../Dialog';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { SettingsLayout } from './layout/SettingsLayout';
import { SettingsProps } from './Settings';
import { useSettingsStore, SettingsStoreProvider } from './store/SettingsStore';
import { MessageDialog, useMessageDialogText } from '../MessageDialog';
import { AvatarSelectorDataProvider } from '../AvatarSelector';
import { getSettingsDialogDataProvider } from './__internal__/SettingsDialogDataProvider';
import { Group } from './model/Group';
import fallbackSettingsDialogDataProvider from './__internal__/FallbackSettingsDialogDP';
import { GlobalSettingsKeys } from '.';

import bundle from './NovaComponents-Settings-gui-icu.properties';

export type Theme = { id: string; text: string };

export interface SettingsDialogDataProvider extends AvatarSelectorDataProvider {
  // returns the system themes to be shown in the SettingsDialog
  getSystemThemes: () => Promise<Array<Theme>>;
  // returns the custom themes to be shown in the SettingsDialog
  getCustomThemes: () => Promise<Array<Theme>>;
  // returns the name of the default configured theme by the system admin.
  getDefaultTheme: () => Promise<string>;
  // returns global settings that should be hidden. Currently supports only
  // Avatar and Offline Processes locale settings as optional global settings.
  getHiddenGlobalSettings?: () => Array<
    GlobalSettingsKeys.Avatar | GlobalSettingsKeys.OFFLINE_LOCALE_KEY
  >;
}

const SettingsDialogDataProviderContext = createContext<NonNullable<SettingsProps['dataProvider']>>(
  fallbackSettingsDialogDataProvider
);

export function useSettingsDialogDataProvider() {
  return useContext(SettingsDialogDataProviderContext);
}

const useTextLocalizer = createUseTextLocalizer(bundle);

export interface SettingsDialogProps {
  dataProvider?: SettingsDialogDataProvider;
  onClose: () => void;
  customForms?: Array<Group>;
  isOpen: boolean;
}

interface SettingsDialogContentProps {
  dataProvider?: SettingsDialogDataProvider;
  onClose: () => void;
  customForms?: Array<Group>;
  isDirty: boolean;
  setDirty: (a: boolean) => void;
}

interface SettingsDialogRef {
  focusOnLayout: () => void;
  hasDirtyFields: () => boolean;
}

const SettingsDialogContent = forwardRef<SettingsDialogRef, SettingsDialogContentProps>(
  (props, ref) => {
    const localize = useTextLocalizer();
    const { CONTINUE, CANCEL } = useMessageDialogText();
    const dataProvider = props.dataProvider ?? getSettingsDialogDataProvider();
    const store = useSettingsStore();
    const [shouldClose, setShouldClose] = useState(false);
    const closeHandle = props.onClose;

    const layoutRef = useRef<HTMLElement | null>(null);

    const hasDirtyFields = useCallback(() => {
      return store.hasDirtyFields();
    }, [store]);

    const focusOnLayout = () => {
      layoutRef.current?.focus();
    };

    useEffect(() => {
      if (shouldClose) {
        closeHandle();
      }
      return () => {
        setShouldClose(false);
      };
    }, [shouldClose, closeHandle]);

    useImperativeHandle(
      ref,
      () => ({
        hasDirtyFields,
        focusOnLayout,
      }),
      [hasDirtyFields]
    );

    return (
      <Fragment>
        <MessageDialog
          testId="settings-dismissAction-formInError"
          type="warning"
          isOpen={props.isDirty}
          title={localize('settings.dismissAction.formInError.dialog.title')}
          text={localize('settings.formInError.warning.msg')}
          firstAction={{
            text: CONTINUE,
            onPress: () => {
              store.clearDirtyFields();
              props.setDirty(false);
              setShouldClose(true);
            },
          }}
          dismissAction={{
            text: CANCEL,
            onPress: () => {
              props.setDirty(false);
            },
            isDefault: true,
          }}
        />
        <SettingsDialogDataProviderContext.Provider value={dataProvider}>
          <SettingsLayout
            testId="settings-layout"
            initialFocusRef={layoutRef}
            customForms={props.customForms}
          />
        </SettingsDialogDataProviderContext.Provider>
      </Fragment>
    );
  }
);

export const SettingsDialog = (props: SettingsDialogProps) => {
  const localize = useTextLocalizer();
  const settingDialogRef = useRef<SettingsDialogRef>({
    hasDirtyFields: () => false,
    focusOnLayout: () => undefined,
  });

  const [isDirty, setDirty] = useState(false);

  function closeHandler() {
    if (settingDialogRef.current.hasDirtyFields()) {
      setDirty(true);
    } else {
      props.onClose();
    }
  }
  /*
    Dialog maxHeight and maxWidth needs some improvement and will remove this once dialog handles it properly. 
    So temporary adding the below maxHeight and maxWidth calculation.
  */
  return (
    <Dialog
      testId="settings"
      size={{
        width: 960,
        height: 768,
        maxWidth: 'calc(100% - 4rem)',
        maxHeight: 'calc(100% - 4rem)',
      }}
      isOpen={props.isOpen}
      title={localize('settings.dialog.title')}
      dismissAction={{
        text: localize('settings.dialog.dismissAction.button.label'),
        onPress: closeHandler,
      }}
      onOpen={() => settingDialogRef.current?.focusOnLayout()}
      focusLockProps={{ autoFocus: false }}
    >
      <SettingsStoreProvider>
        <SettingsDialogContent
          ref={settingDialogRef}
          customForms={props.customForms}
          dataProvider={props.dataProvider}
          isDirty={isDirty}
          setDirty={setDirty}
          onClose={props.onClose}
        />
      </SettingsStoreProvider>
    </Dialog>
  );
};
